import Highcharts from "highcharts"
import type {
  XAxisPlotBandsOptions,
  YAxisPlotBandsOptions,
  SeriesOptionsType,
  XAxisPlotLinesOptions,
  YAxisPlotLinesOptions,
  DashStyleValue,
  AnimationOptionsObject,
  CSSObject,
  DataLabelsOptions,
} from "highcharts"
import "highcharts/highcharts-more"
import "highcharts/modules/draggable-points"
import "highcharts/modules/heatmap"
import "highcharts/modules/pattern-fill"
import { HighchartsReact, type HighchartsReactRefObject } from "highcharts-react-official"
import { max, maxBy, min } from "lodash"

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react"

import { type DateRange } from "~/stores/dateRangeStore"
import {
  getEndOfCurrentMonthUTC,
  getStartOfCurrentMonthUTC,
  startOfDayUTC,
  startOfTodayUTC,
} from "~/utils/datetime"
import { formatNumberWithSuffix } from "~/utils/formatting"
import { round } from "~/utils/math"

import {
  generateDateCategoryChartLegend,
  generateDoughnutChartLegend,
  generateMixedChartLegend,
} from "./utils/GraphLegendUtils"
import { createYearPlotLines } from "./utils/GraphPlotLineUtils"
import { shiftPlotLineLabelsClearOfColumns } from "./utils/plotLineLabelClearance"
import {
  generateDateHighchartsSeries,
  generateHighchartsCategorySeries,
  generateHighchartsHeatmapSeries,
  generateHighchartsMixedSeries,
  generateHighchartsPieSeries,
  generateScatterHighchartsSeries,
  generateStackedColumnHighchartsSeries,
  generateWaterfallHighchartsSeries,
} from "./utils/GraphSeriesUtils"
import {
  dateGraphTooltipFormatter,
  doughnutGraphTooltipFormatter,
  scatterGraphTooltipFormatter,
} from "./utils/GraphTooltipUtils"
import { getTailwindValue } from "~/utils/tailwind"
import { useTheme } from "next-themes"
import { getChartBaseColors, type ChartSeriesPattern } from "~/constants/colors"
import { ChartType, DateType, LegendAlignment, TodayPlotLine } from "~/constants/graphs"
import { api } from "~/utils/api"
import { cn } from "~/utils/helpers"
import { useStableValue } from "~/hooks/useStableValue"

export interface DateChartSeries {
  name: string
  displayName?: string
  data: {
    milliseconds: number
    value: number | null
    /**
     * Optional value used by the shared tooltip formatter instead of `value`.
     * Useful when a series renders a stacking/overlay height (`value`) different
     * from the actual quantity to display in the tooltip.
     */
    tooltipValue?: number | null
    color?: string
    /**
     * Renders the point as a solid drag handle (series-colored circle with a background-colored
     * ring). Marks a value the user has explicitly set on an editable series — a pinned week the
     * curve must pass through — as opposed to the computed points, which stay markerless.
     */
    pinned?: boolean
  }[]
  type: "spline" | "areaspline" | "column"
  colors: string[]
  dashStyle?: DashStyleValue
  lineWidth?: number
  threshold?: number
  yAxis?: number
  showNegativeColor?: boolean
  /**
   * Makes the series' points vertically draggable (drag-to-edit). `min`/`max` clamp the value a
   * drag can produce. Pair with `onPointDrop` — dragging without a drop handler edits nothing.
   * Gate on `permissions.canEdit` when the drop commits a mutation, like any editing control.
   */
  draggableY?: { min?: number; max?: number }
  /** Fires when a drag ends: the point's x (epoch ms) and the value it was dropped at. */
  onPointDrop?: (milliseconds: number, value: number) => void
  /** Fires on point click — e.g. select the period a drag would edit. */
  onPointClick?: (milliseconds: number) => void
  custom?: {
    legendTooltip?: string
    valueFormatter?: (num: number) => string
  }
  disabled?: boolean
  /** Left out of the custom legend, e.g. a second segment of a series another entry already names. */
  hideInLegend?: boolean
  hidden?: boolean
  stack?: string
  stacking?: "normal" | "percent"
  grouping?: boolean
  pattern?: ChartSeriesPattern
}

export interface StackedBarChartData {
  name: string
  data: (number | null)[]
  color: string
  type: "column"
  /**
   * Per-point callout text, index-aligned with `data`. A non-null entry renders that text
   * above its column; null leaves the point unlabelled. Use for something the point's own
   * value cannot say — a delta against another column, say — which is what `showDataLabels`
   * (whole-chart, value-only) cannot express. Kept separate from `data` so callers that do
   * arithmetic over `data` are unaffected.
   */
  pointLabels?: (string | null)[]
}

export interface CategoryChartSeries {
  name: string
  displayName?: string
  data: { category: string; value: number | null; color?: string }[]
  type: "column" | "spline" | "areaspline" // Add more types if needed
  colors: (string | Highcharts.GradientColorObject | Highcharts.PatternObject)[]
  dashStyle?: DashStyleValue
  lineWidth?: number
  custom?: {
    legendTooltip?: string
  }
  yAxis?: number
  disabled?: boolean
}

export interface BarChartData {
  name: string
  data: number
  type: "bar"
}

export interface WaterfallChartData {
  name: string
  data?: {
    name: string
    y: number
    isIntermediateSum?: boolean
    color?: string
    dataLabels?: DataLabelsOptions
  }[]
}

export interface PieChartData {
  name: string
  data: number
  color: string
  custom?: {
    legendTooltip?: string
    truncate?: boolean
    length?: number
    dontShowData?: boolean
    /** When set, legend + slice tooltip show both metrics at once; `active` is the one sizing the slices. */
    dualReadout?: { count: number; share: number; active: "count" | "share" }
  }
  disabled?: boolean
}

export interface HeatMapData {
  x: number
  y: number
  value: number | null
}

export interface PlotLine {
  value: number // In milliseconds
  lineColor: string
  text?: string
  // Only shows the label if the textColor and backgroundColor is set
  textColor?: string
  backgroundColor?: string
  dashStyle?: DashStyleValue
  width?: number
  zIndex?: number
  showOnHover?: boolean
  labelAlign?: "left" | "right" | "center"
  rotation?: number
  offset?: { x?: number; y?: number }
  labelStyle?: CSSObject
  /** Keeps the label visible when it overlaps a neighbouring plot line label instead of letting the chart hide one of them. */
  allowLabelOverlap?: boolean
  /** Native hover text for the label, for when `text` is a shortened form. */
  labelTitle?: string
}

export interface PlotBand {
  from: number
  to: number
  color: string
  text?: string
  textColor?: string
  borderColor?: string
  /** Vertical label offset — lets overlapping bands stack their labels instead of overprinting. */
  labelY?: number
}

export type MixedChartData = SeriesOptionsType & {
  name: string
  disabled?: boolean
}

export interface ScatterPointData {
  x: number
  y: number
  name?: string
  variantId?: string
  variantName?: string
  productName?: string
  imageUrl?: string
}

export interface ScatterChartData {
  name: string
  data: ScatterPointData[]
  color?: string
  disabled?: boolean
  regressionLine?: { x: number; y: number }[]
}

type SuperChartProps =
  | ({ type: ChartType.TimeSeries; data: BarChartData[] } & CommonChartProps)
  | ({ type: ChartType.StackedColumn; data: StackedBarChartData[] } & CommonChartProps)
  | ({ type: ChartType.Pie; data: PieChartData[] } & CommonChartProps)
  | ({ type: ChartType.Heatmap; data: HeatMapData[] } & CommonChartProps)
  | ({ type: ChartType.Waterfall; data: WaterfallChartData[] } & CommonChartProps)
  | ({ type: ChartType.TimeSeries; data: DateChartSeries[] } & CommonChartProps)
  | ({ type: ChartType.CategorySeries; data: CategoryChartSeries[] } & CommonChartProps)
  | ({ type: ChartType.Mixed; data: MixedChartData[] } & CommonChartProps)
  | ({ type: ChartType.Scatter; data: ScatterChartData[] } & CommonChartProps)

interface CommonChartProps {
  theme?: "light" | "dark"
  xCategories?: string[]
  yCategories?: string[]
  dateRange?: DateRange
  dateType?: DateType
  height?: number
  compact?: boolean
  title?: string
  subtitleLabel?: string
  numberOfData?: number
  useDefaultLegend?: boolean
  legendAlignment?: LegendAlignment
  centerLegend?: boolean
  noLegend?: boolean
  todayPlotLine?: TodayPlotLine
  todayPlotLineValue?: number
  showYearPlotLines?: boolean
  plotLines?: { x?: PlotLine[]; y?: PlotLine[] }
  plotBands?: { x?: PlotBand[]; y?: PlotBand[] }
  colors?: (string | Highcharts.GradientColorObject | Highcharts.PatternObject)[]
  colorAxis?: Highcharts.ColorAxisOptions | Highcharts.ColorAxisOptions[] | undefined
  dataAsPercentage?: boolean
  showCurrency?: boolean
  showAllDigits?: boolean
  isStacked?: boolean
  stackSectionLabels?: Record<string, string>
  unsectionedLabel?: string
  /** Categories whose tooltip states the column's total alone, with no series rows. */
  plainTooltipCategories?: readonly string[]
  disableAnimation?: boolean
  animationDuration?: number
  zoomEnabled?: boolean
  showTooltip?: boolean
  yAxis?: Highcharts.YAxisOptions | Highcharts.YAxisOptions[]
  showYAxisLabels?: boolean
  showXAxisLabels?: boolean
  showTickLines?: boolean
  margin?: number[]
  plotTopMargin?: number
  xAxisLabelSteps?: number
  xAxisYOffset?: number
  onClick?: (e: any) => void
  selectionEvent?: Highcharts.ChartSelectionCallbackFunction
  customTooltipFormatter?: (this: Highcharts.Point) => string
  /**
   * The hovered point's index in its series, then `null` once the pointer leaves the chart's
   * container. With one point per x in every series, the index is the hovered x's position.
   */
  onPointHover?: (index: number | null) => void
  /**
   * Puts the points at this index in their hover state, `null` clears it. For keyboard stepping
   * (`useChartReadout().highlightIndex`); the pointer highlights on its own.
   */
  highlightIndex?: number | null
  /**
   * While `highlightIndex` names a period, column points at other periods drop to 40% opacity so
   * the active one stands out. No effect without `highlightIndex`.
   */
  dimOthersOnHighlight?: boolean
  /**
   * Moves right-aligned y-axis plot line labels left until no column is under them, after every
   * render (Highcharts re-aligns them on each redraw).
   */
  clearPlotLineLabels?: boolean
  /**
   * Controls left of the legend in one toolbar row. Passing `toolbar` or `readout` lays the chart
   * out as toolbar row → readout → plot. Time series, category and mixed charts only.
   */
  toolbar?: ReactNode
  /**
   * A `ChartReadout`, rendered between the toolbar row and the plot. While only the frame props
   * change (`readout`, `toolbar`, `plotProps`, `highlightIndex`) the plot isn't updated, so keep the
   * other props referentially stable.
   */
  readout?: ReactNode
  /** Spread on the element wrapping the plot only, e.g. `useChartReadout().plotProps`. */
  plotProps?: HTMLAttributes<HTMLDivElement>
  fullColumnWidth?: boolean
  minColumnPointLength?: number
  /** Caps a column's width in px, so a few periods don't draw slabs. */
  maxColumnWidth?: number
  showDataLabels?: boolean
  /**
   * Pins the y-axis ceiling instead of letting Highcharts derive it from the data. Use when
   * the same chart re-renders with different values — a toggle switching between scenarios,
   * say — and the axis rescaling between renders would make the columns incomparable.
   */
  yAxisMax?: number

  xAxisTitle?: string
  yAxisTitle?: string

  xAxisLabelFormatter?: (this: Highcharts.AxisLabelsFormatterContextObject) => string

  showRegression?: boolean
  scatterMarkerRadius?: number
  regressionLineColor?: string
  xAxisType?: "datetime" | "linear"
  xAxisTickPositions?: number[]
  xAxisTickInterval?: number
  xAxisLabelOverflow?: "justify" | "allow"
  autoFitXLabels?: boolean
}

const graphColors = (theme: "light" | "dark") => {
  return {
    background: "transparent",
    gridLine:
      theme === "light"
        ? getTailwindValue("--color-gray-200")
        : getTailwindValue("--color-white-alpha-10"),
    axisText:
      theme === "light"
        ? getTailwindValue("--color-gray-600")
        : getTailwindValue("--color-gray-400"),
    title:
      theme === "light"
        ? getTailwindValue("--color-gray-950")
        : getTailwindValue("--color-gray-100"),
    // Figma's foregrounds/fg-base; branched explicitly rather than read from --color-fg-base,
    // which goes stale on theme toggle.
    legendText:
      theme === "light"
        ? getTailwindValue("--color-gray-950")
        : getTailwindValue("--color-gray-100"),
    pieBorder: theme === "light" ? "#fff" : getTailwindValue("--color-gray-900"),
  }
}

const pointHoverEvents = (onPointHover: (index: number | null) => void) => ({
  point: {
    events: {
      mouseOver: function (this: Highcharts.Point) {
        onPointHover(this.index)
      },
    },
  },
})

const DIMMED_OPACITY = 0.4

/** Puts the highlighted period's points in their hover state and, if asked, dims other columns. */
const applyHighlight = (
  chart: Highcharts.Chart,
  { index, dims }: { index: number | null | undefined; dims: boolean | undefined },
) => {
  if (index === undefined) return
  for (const series of chart.series) {
    const dimsSeries = dims && series.type === "column"
    for (const point of series.points ?? []) {
      point.setState(point.index === index ? "hover" : "")
      if (dimsSeries) {
        const dimmed = index != null && point.index !== index
        point.graphic?.attr({ opacity: dimmed ? DIMMED_OPACITY : 1 })
      }
    }
  }
}

// Series mouseOut misses a pointer leaving across the plot edge; the container's leave doesn't.

const hoverContainerProps = (onPointHover?: (index: number | null) => void) =>
  onPointHover ? { onMouseLeave: () => onPointHover(null) } : undefined

function chartAnimation(
  disabled: boolean | undefined,
  duration: number | undefined,
): boolean | AnimationOptionsObject {
  if (disabled) return false
  if (duration == null) return true
  return { duration }
}

// Left is the default `flex-row`; the inline `alignItems` decides cross-axis placement.
const SIDE_LEGEND_DIRECTION: Partial<Record<LegendAlignment, string>> = {
  [LegendAlignment.Right]: "flex-row-reverse",
  [LegendAlignment.Bottom]: "flex-col-reverse",
}

const FRAME_PROPS = new Set<string>(["readout", "toolbar", "plotProps", "highlightIndex"])

const sameExceptFrame = (a: SuperChartProps | null, b: SuperChartProps | null) => {
  if (a == null || b == null) return a === b
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return [...keys].every(
    (key) =>
      FRAME_PROPS.has(key) || a[key as keyof SuperChartProps] === b[key as keyof SuperChartProps],
  )
}

/**
 * Hovering a readout chart re-renders it with a new readout on every point. `chartKey` stays the
 * previous props object while only the frame props change, so the plot keeps its options and
 * Highcharts skips `chart.update`. Charts without a readout or toolbar key on their props as before.
 */
const SuperChart = (props: SuperChartProps) => {
  const hasFrame = props.readout != null || props.toolbar != null
  const chartKey = useStableValue(hasFrame ? props : null, sameExceptFrame) ?? props
  return <SuperChartView {...props} chartKey={chartKey} />
}

const SuperChartView = ({
  chartKey,
  type,
  height,
  legendAlignment,
  noLegend,
  useDefaultLegend,
  subtitleLabel,
  xCategories,
  dateRange,
  compact,
  margin = undefined,
  todayPlotLine = TodayPlotLine.Basic,
  dateType = DateType.Weekly,
  centerLegend = false,
  isStacked = false,
  showTooltip = true,
  yAxis,
  showYAxisLabels = true,
  showXAxisLabels = true,
  showTickLines = true,
  xAxisLabelSteps = 0,
  ...props
}: SuperChartProps & { chartKey: SuperChartProps }) => {
  const animation = chartAnimation(props.disableAnimation, props.animationDuration)
  const { resolvedTheme } = useTheme()
  const { data: company } = api.company.getCompany.useQuery()

  const seriesNames = (data: SuperChartProps["data"]) =>
    data.filter((entry: any) => entry.name != null).map((entry: any) => entry.name as string)

  const defaultDisabledNames = (data: SuperChartProps["data"]) =>
    data
      .filter((entry: any) => entry.hidden || entry.disabled)
      .map((entry: any) => entry.name as string)

  const [disabledIds, setDisabledIds] = useState<string[]>(() => defaultDisabledNames(props.data))
  const [seenNames, setSeenNames] = useState<string[]>(() => seriesNames(props.data))

  // Series can appear after mount (async data, filter changes); they must still respect their
  // hidden/disabled default, while manual legend toggles on already-seen series are kept.
  const newNames = seriesNames(props.data).filter((name) => !seenNames.includes(name))
  if (newNames.length > 0) {
    setSeenNames([...seenNames, ...newNames])
    const newDisabled = defaultDisabledNames(props.data).filter((name) => newNames.includes(name))
    if (newDisabled.length > 0) setDisabledIds([...disabledIds, ...newDisabled])
  }

  const chartRef = useRef<HighchartsReactRefObject>(null)

  useLayoutEffect(() => {
    const container = chartRef.current?.container.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      const containerWidth = chartRef.current?.container.current?.offsetWidth
      chartRef.current?.chart.setSize(containerWidth, chartRef.current?.chart.chartHeight, false)
    })

    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  const { highlightIndex, dimOthersOnHighlight } = props
  const highlightRef = useRef({ index: highlightIndex, dims: dimOthersOnHighlight })
  const clearLabelsRef = useRef(props.clearPlotLineLabels)
  useEffect(() => {
    clearLabelsRef.current = props.clearPlotLineLabels
  }, [props.clearPlotLineLabels])
  useEffect(() => {
    highlightRef.current = { index: highlightIndex, dims: dimOthersOnHighlight }
    const chart = chartRef.current?.chart
    if (chart) applyHighlight(chart, highlightRef.current)
  }, [highlightIndex, dimOthersOnHighlight])
  // Every re-render updates the chart, and its redraw animates each point back to its default
  // look within a second; re-applied on render, the highlight stays (an attr stops that animation).
  const onRender = useCallback(function (this: Highcharts.Chart) {
    applyHighlight(this, highlightRef.current)
    if (clearLabelsRef.current) shiftPlotLineLabelsClearOfColumns(this)
  }, [])

  const finalData = props.data.map((entry) => {
    return Object.prototype.hasOwnProperty.call(entry, "name")
      ? {
          ...entry,
          disabled: disabledIds.includes((entry as any).name),
        }
      : entry
  })

  let chartHeight = height
  const theme = resolvedTheme ?? "light"
  let width: number | undefined = undefined
  let series: SeriesOptionsType[] = []
  let spacing = [0, 0, 0, 0]
  let xAxisOpposite = false
  const cursor = props.onClick ? "pointer" : "default"
  let useColorAxis = false // Heatmap only - Disrupts legend if used elsewhere
  // Stacked column only — set where the union is already narrowed, since the yAxis config
  // below cannot narrow props.data itself.
  let hasPointLabels = false
  let yGridLineWidth = 1
  let yAxisCount = 1
  const colors = graphColors(theme as "light" | "dark")

  switch (type) {
    case ChartType.Pie:
      height = height || 200
      width = height
      series = generateHighchartsPieSeries(finalData as PieChartData[], subtitleLabel)
      margin = [props.title ? 20 : 0, 0, 0, 0]
      useDefaultLegend = false
      legendAlignment =
        legendAlignment && legendAlignment !== LegendAlignment.Top
          ? legendAlignment
          : LegendAlignment.Left
      centerLegend = true
      yGridLineWidth = 0
      isStacked = false
      break
    case ChartType.Heatmap:
      height = height || 400
      margin = undefined
      noLegend = true
      subtitleLabel = undefined
      xAxisOpposite = true
      series = generateHighchartsHeatmapSeries(finalData as HeatMapData[])
      useColorAxis = true
      yGridLineWidth = 0
      isStacked = false
      break
    case ChartType.TimeSeries:
      legendAlignment = legendAlignment ?? LegendAlignment.Top
      height = height ?? 300
      compact = compact ?? height < 200
      noLegend = noLegend ?? compact
      chartHeight = !noLegend && !useDefaultLegend ? height - 28 : height
      spacing = [0, 2, 0, 2]
      margin = [props.plotTopMargin ?? (props.title ? 34 : 4)]
      series = generateDateHighchartsSeries(
        finalData as DateChartSeries[],
        animation,
        // Pinned drag handles share the pie-border treatment: a ring the color of the page
        // background, so the handle reads as punched out of the line. Stack seams take the same
        // tone for the same reason — one surface color, two uses.
        colors.pieBorder,
        colors.pieBorder,
      )
      subtitleLabel = undefined
      todayPlotLine = todayPlotLine ?? TodayPlotLine.Basic
      yAxisCount = (maxBy(props.data as DateChartSeries[], "yAxis")?.yAxis ?? 0) + 1
      yGridLineWidth = showTickLines === false ? 0 : 1

      const allMilliseconds = (finalData as DateChartSeries[])
        .filter((d) => !d.disabled)
        .flatMap((series) => series.data?.map((point) => point.milliseconds))
      const minDate = min(allMilliseconds)
      const maxDate = max(allMilliseconds)
      dateRange = dateRange ?? {
        start: minDate ? startOfDayUTC(minDate) : getStartOfCurrentMonthUTC(),
        end: maxDate ? startOfDayUTC(maxDate) : getEndOfCurrentMonthUTC(),
      }

      break
    case ChartType.CategorySeries:
      legendAlignment = legendAlignment ?? LegendAlignment.Top
      height = height ?? 300
      compact = compact ?? height < 200
      noLegend = noLegend ?? compact
      chartHeight = !noLegend && !useDefaultLegend ? height - 28 : height
      margin = margin ? margin : props.title ? [34] : [4]
      spacing = [0, 5, 0, 5]
      xCategories =
        (finalData as CategoryChartSeries[])[0]?.data.map((entry) => entry.category) || []
      yAxisCount = (maxBy(props.data as CategoryChartSeries[], "yAxis")?.yAxis ?? 0) + 1
      series = generateHighchartsCategorySeries(finalData as CategoryChartSeries[], animation)
      yGridLineWidth = showTickLines === false ? 0 : 1
      break
    case ChartType.StackedColumn:
      legendAlignment = legendAlignment ?? LegendAlignment.Top
      height = height ?? 300
      compact = compact ?? height < 200
      noLegend = noLegend ?? compact
      chartHeight = !noLegend && !useDefaultLegend ? height - 28 : height
      margin = margin ? margin : props.title ? [34] : [4]
      spacing = [0, 5, 0, 5]
      hasPointLabels = (finalData as StackedBarChartData[]).some((entry) =>
        entry.pointLabels?.some((label) => label != null),
      )
      series = generateStackedColumnHighchartsSeries(finalData as StackedBarChartData[], {
        color: colors.axisText,
        fontSize: compact ? "9px" : "11px",
      })
      yGridLineWidth = showTickLines === false ? 0 : 1
      isStacked = true
      break
    case ChartType.Waterfall:
      legendAlignment = legendAlignment ?? LegendAlignment.Top
      height = height ?? 300
      compact = compact ?? height < 200
      noLegend = noLegend ?? compact
      chartHeight = !noLegend && !useDefaultLegend ? height - 28 : height
      margin = margin ? margin : props.title ? [34] : [4]
      spacing = [0, 5, 0, 5]
      series = generateWaterfallHighchartsSeries(finalData as WaterfallChartData[])
      yGridLineWidth = showTickLines === false ? 0 : 1
      // Waterfalls are intrinsically cumulative; forcing stacking="normal" makes
      // highcharts-more's stack `order` handler throw on multi-step/negative data.
      isStacked = false
      break
    case ChartType.Mixed:
      legendAlignment = legendAlignment ?? LegendAlignment.Top
      height = height ?? 300
      compact = compact ?? height < 200
      noLegend = noLegend ?? compact
      chartHeight = !noLegend && !useDefaultLegend ? height - 28 : height
      margin = margin ? margin : props.title ? [34] : [4]
      spacing = [0, 5, 0, 5]
      series = generateHighchartsMixedSeries(
        finalData as MixedChartData[],
        getChartBaseColors(theme as "light" | "dark").base,
      )
      xCategories = xCategories
      yGridLineWidth = showTickLines === false ? 0 : 1
      yAxisCount = Number(maxBy(props.data as MixedChartData[], "yAxis")?.yAxis ?? 0) + 1
      isStacked = true
      break
    case ChartType.Scatter:
      legendAlignment = legendAlignment ?? LegendAlignment.Top
      height = height ?? 300
      compact = compact ?? height < 200
      noLegend = noLegend ?? compact
      chartHeight = !noLegend && !useDefaultLegend ? height - 28 : height
      margin = margin ? margin : props.title ? [34] : [4]
      spacing = [0, 5, 0, 5]
      series = generateScatterHighchartsSeries(
        finalData as ScatterChartData[],
        props.showRegression ?? true,
        getChartBaseColors(theme as "light" | "dark").base,
        {
          markerRadius: props.scatterMarkerRadius,
          regressionLineColor: props.regressionLineColor,
        },
      )
      yGridLineWidth = showTickLines === false ? 0 : 1

      const allScatterX = (finalData as ScatterChartData[])
        .filter((d) => !d.disabled)
        .flatMap((s) => s.data.map((p) => p.x))
      if (allScatterX.length > 0) {
        const minX = min(allScatterX)
        const maxX = max(allScatterX)
        if (minX && maxX) {
          dateRange = { start: new Date(minX), end: new Date(maxX) }
        }
      }
      break
  }

  // A toolbar or readout uses the top-legend layout.
  const hasFrame = props.toolbar != null || props.readout != null
  if (hasFrame) legendAlignment = LegendAlignment.Top

  let xPlotLines = props.plotLines?.x ?? []
  const yPlotLines = props.plotLines?.y ?? []

  xPlotLines =
    dateRange && props.showYearPlotLines
      ? [...xPlotLines, ...createYearPlotLines(dateRange)]
      : xPlotLines

  if (todayPlotLine !== TodayPlotLine.Hide) {
    const todayPlotLineFinal: PlotLine = {
      value: props.todayPlotLineValue ?? startOfTodayUTC().getTime(),
      lineColor: "rgba(156, 157, 157, 0.6)",
      text: "Today",
    }

    if (todayPlotLine === TodayPlotLine.Detailed) {
      todayPlotLineFinal.backgroundColor = getTailwindValue("--color-background-subtle")
      todayPlotLineFinal.textColor = getTailwindValue("--color-fg-subtle")
      todayPlotLineFinal.offset = { y: 12 }
    }

    xPlotLines = [todayPlotLineFinal, ...xPlotLines]
  }

  const plotLinesX: XAxisPlotLinesOptions[] = xPlotLines.map((line) => {
    const plotLine: XAxisPlotLinesOptions = {
      id: line.text,
      value: line.value,
      color: line.lineColor,
      width: line.width ?? 2,
      zIndex: line.zIndex ?? 3, // Place on top of the grid lines (but below the series)
      dashStyle: line.dashStyle,
    }

    if (line.backgroundColor && line.textColor) {
      plotLine.label = {
        text: line.labelTitle ? `<span title="${line.labelTitle}">${line.text}</span>` : line.text,
        align: line.labelAlign ?? "center",
        rotation: line.rotation ?? 0,
        useHTML: true,
        // Read by Highcharts at runtime but missing from its label option types.
        ...(line.allowLabelOverlap && { allowOverlap: true }),
        y: line.offset?.y ?? 8,
        x: line.offset?.x ?? -2,
        style: {
          backgroundColor: line.backgroundColor,
          color: line.textColor,
          fontSize: "12px",
          fontWeight: "500",
          fontFamily: "Inter, sans-serif",
          padding: "4px 8px",
          "border-radius": "10px",
          "box-shadow": getTailwindValue("--shadow-flyout"),

          display: line.showOnHover ? "none" : "block",
          ...line.labelStyle,
        },
      }
    }

    if (line.showOnHover) {
      plotLine.events = {
        mouseover: function () {
          this.label?.css({ display: "block" })
        },
        mouseout: function () {
          this.label?.css({ display: "none" })
        },
      }
    }

    return plotLine
  })

  const plotLinesY: YAxisPlotLinesOptions[] = yPlotLines.map((line) => {
    return {
      id: line.text,
      value: line.value,
      color: line.lineColor,
      width: 2,
      zIndex: 1, // Place on top of the grid lines (but below the series)
    } as YAxisPlotLinesOptions
  })

  // This is a way to add a dashed border around the plot bands.
  const plotBandLinesX: XAxisPlotLinesOptions[] = (props.plotBands?.x ?? [])
    .filter((band) => band.borderColor)
    .map((band) => [
      {
        value: band.from,
        color: band.borderColor ?? getTailwindValue("--color-gray-300"),
        width: 1,
        dashStyle: "Dash" as DashStyleValue,
      },
      {
        value: band.to,
        color: band.borderColor ?? getTailwindValue("--color-gray-300"),
        width: 1,
        dashStyle: "Dash" as DashStyleValue,
      },
    ])
    .flat()

  const plotBandLinesY: YAxisPlotLinesOptions[] = (props.plotBands?.y ?? [])
    .filter((band) => band.borderColor)
    .map((band) => [
      {
        value: band.from,
        color: band.borderColor ?? getTailwindValue("--color-gray-300"),
        width: 1,
        dashStyle: "Dash" as DashStyleValue,
      },
      {
        value: band.to,
        color: band.borderColor ?? getTailwindValue("--color-gray-300"),
        width: 1,
        dashStyle: "Dash" as DashStyleValue,
      },
    ])
    .flat()

  const plotBandsX: XAxisPlotBandsOptions[] = (props.plotBands?.x ?? []).map((band) => {
    // Nowrap labels overflow the band; for bands in the right quarter of the axis,
    // extend the label leftward so it isn't clipped by the chart edge.
    const nearRightEdge =
      dateRange &&
      (band.from + band.to) / 2 >
        dateRange.start.getTime() + 0.75 * (dateRange.end.getTime() - dateRange.start.getTime())
    return {
      from: band.from,
      to: band.to,
      color: band.color,
      label: band.text
        ? {
            text: band.text,
            style: {
              color: band.textColor ?? getTailwindValue("--color-fg-base"),
              fontWeight: "600",
              fontSize: "12px",
              fontFamily: "InterDisplay, sans-serif",
              // Labels of narrow bands otherwise wrap and ellipsize into an unreadable
              // one-character column; let them overflow the band instead.
              whiteSpace: "nowrap",
              textOverflow: "none",
            },
            align: nearRightEdge ? "right" : "left",
            textAlign: nearRightEdge ? "right" : "left",
            x: nearRightEdge ? -8 : 8,
            y: band.labelY ?? 20,
          }
        : undefined,
      zIndex: 1,
    }
  })
  const plotBandsY: YAxisPlotBandsOptions[] = (props.plotBands?.y ?? []).map((band) => ({
    from: band.from,
    to: band.to,
    color: band.color,
    label: band.text
      ? {
          text: band.text,
          align: "left",
          x: 8, // These should perhaps be modified. We are not currently using the y plot bands anywhere.
          y: 8,
        }
      : undefined,
    zIndex: 1,
  }))

  const options: Highcharts.Options = useMemo(() => {
    return {
      chart: {
        height: chartHeight,
        width: width,
        animation,
        spacing: spacing,
        backgroundColor: colors.background,
        margin: margin,
        style: {
          fontFamily: "Inter, sans-serif",
        },
        zooming: {
          type: props.selectionEvent || props.zoomEnabled ? "x" : undefined,
        },
        events: {
          selection: props.selectionEvent,
          click: props.onClick,
          render:
            props.highlightIndex === undefined && !props.clearPlotLineLabels ? undefined : onRender,
        },
      },
      series: series,
      title: props.title
        ? {
            text: props.title,
            align: "left",
            x: 0,
            y: 12,
            widthAdjust: 0,
            floating: true,
            style: {
              color: colors.title,
              fontSize: "13px",
              fontWeight: "500",
            },
          }
        : undefined,
      subtitle:
        props.numberOfData === undefined || subtitleLabel === undefined
          ? undefined
          : {
              useHTML: true,
              text: `<div>
              <div style="font-size: 16px; font-weight: 500; color: var(--color-fg-base); text-align:center">${formatNumberWithSuffix(props.numberOfData ?? 0, 1)}</div>
              <div style="margin-top:4px;font-size: 12px; color: var(--color-fg-muted); text-align: center">
                ${subtitleLabel}
              </div>
            </div>`,
              floating: false,
              align: "center",
              verticalAlign: "middle",
              y: props.title ? 26 : 16,
              x: 0,
            },
      plotOptions: {
        series: {
          stacking: isStacked ? "normal" : undefined,
          // Every marker is a circle. Without this Highcharts cycles symbols by series index
          // (diamond, square, triangle…), which surfaces on hover halos and legend symbols.
          marker: {
            symbol: "circle",
          },
          states: {
            inactive: {
              enabled: false,
            },
          },
          ...(props.onPointHover ? pointHoverEvents(props.onPointHover) : {}),
        },
        area: {
          stacking: isStacked ? "normal" : undefined,
          marker: {
            enabled: false,
          },
        },
        column: {
          stacking: isStacked ? "normal" : undefined,
          borderColor: "transparent",
          borderRadius: 6,
          minPointLength: props.minColumnPointLength,
          maxPointWidth: props.maxColumnWidth,
          ...(props.fullColumnWidth
            ? {
                pointPadding: 0,
                borderWidth: 0,
                groupPadding: 0,
                borderRadius: 0,
              }
            : {}),
          shadow: false,
          dataLabels: props.showDataLabels
            ? {
                enabled: true,
                crop: false,
                overflow: "allow",
                inside: false,
                style: {
                  fontSize: compact ? "9px" : "11px",
                  fontWeight: "600",
                  color: colors.axisText,
                  textOutline: "none",
                },
              }
            : undefined,
        },
        pie: {
          allowPointSelect: true,
          animation,
          dataLabels: {
            enabled: false,
          },
          borderColor: colors.pieBorder,
          size: "100%",
          events: {
            click:
              props.onClick ??
              function (e) {
                e.preventDefault() // Prevent that the selected donut piece floats away from the graph.
              },
          },
        },
        heatmap: {
          allowPointSelect: false,
          cursor: cursor,
          borderRadius: 4,
          pointPadding: 3,
          // A null point has no value on the scale, so it sits outside the colour axis entirely
          // rather than colouring as its bottom class.
          nullColor: getTailwindValue("--color-badge-neutral-bg"),
          events: {
            click: props.onClick,
          },
        },
      },
      xAxis: {
        min:
          props.xAxisType === "linear"
            ? undefined
            : dateRange
              ? dateRange.start.getTime()
              : undefined,
        max:
          props.xAxisType === "linear"
            ? undefined
            : dateRange
              ? dateRange.end.getTime()
              : undefined,
        categories: xCategories,
        opposite: xAxisOpposite,
        type: xCategories ? "category" : (props.xAxisType ?? "datetime"),
        lineWidth: 0,
        gridLineWidth: 0,
        minPadding: props.xAxisType === "linear" ? 0.02 : undefined,
        maxPadding: props.xAxisType === "linear" ? 0.02 : 1,
        tickColor: colors.gridLine,
        tickPixelInterval: compact ? 70 : 50,
        tickInterval: props.xAxisTickInterval,
        tickWidth: showTickLines ? 1 : 0,
        tickPositions: props.xAxisTickPositions,
        labels: {
          enabled: showXAxisLabels,
          staggerLines: props.autoFitXLabels ? undefined : 1,
          step: xAxisLabelSteps,
          allowOverlap: false,
          overflow: props.xAxisLabelOverflow ?? (props.autoFitXLabels ? "justify" : "allow"),
          rotation: props.xAxisType === "linear" ? -45 : props.autoFitXLabels ? -45 : undefined,
          align: props.xAxisType === "linear" || props.autoFitXLabels ? "right" : undefined,
          style: {
            fontWeight: "400",
            fontSize: compact ? "10px" : "12px",
            color: colors.axisText,
            textOverflow: "none",
          },
          y: props?.xAxisYOffset,
          formatter: props?.xAxisLabelFormatter ?? undefined,
        },
        title: props.xAxisTitle
          ? {
              text: props.xAxisTitle,
              style: {
                color: colors.axisText,
                fontSize: compact ? "10px" : "12px",
                fontWeight: "500",
              },
            }
          : undefined,
        // TODO: check if this works as intended
        plotLines: [...plotLinesX, ...plotBandLinesX],
        plotBands: [...plotBandsX, ...plotBandLinesX],
      },
      yAxis: Array.isArray(yAxis)
        ? yAxis.map((axis, index) => ({
            lineWidth: 0,
            gridLineWidth: yGridLineWidth,
            gridLineColor: colors.gridLine,
            title: undefined,
            tickPixelInterval: showTickLines === false ? 0 : 36,
            labels: {
              enabled: showYAxisLabels,
              style: {
                fontWeight: "400",
                fontSize: compact ? "10px" : "12px",
                color: colors.axisText,
              },
              ...(props.dataAsPercentage
                ? {
                    formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                      return (round(Number(this.value) * 100, 3) + "%").toString()
                    },
                  }
                : {}),
            },
            plotLines: [...plotLinesY, ...plotBandLinesY],
            plotBands: plotBandsY,
            opposite: index > 0,
            max: props.yAxisMax,
            reversedStacks: false,
            ...axis,
          }))
        : Array.from({ length: yAxisCount }).map((_, index) => ({
            categories: props.yCategories,
            lineWidth: 0,
            gridLineWidth: yGridLineWidth,
            gridLineColor: colors.gridLine,
            title: props.yAxisTitle
              ? {
                  text: props.yAxisTitle,
                  style: {
                    color: colors.axisText,
                    fontSize: compact ? "10px" : "12px",
                    fontWeight: "500",
                  },
                }
              : undefined,
            tickPixelInterval: showTickLines === false ? 0 : 36,
            // Headroom so labels sitting above the tallest column are not clipped. Point
            // callouts need it for the same reason whole-chart data labels do. An explicit
            // ceiling wins over padding, so skip it there — the caller sized the axis.
            maxPadding:
              props.yAxisMax == null && (props.showDataLabels || hasPointLabels) ? 0.2 : undefined,
            max: props.yAxisMax,
            labels: {
              enabled: showYAxisLabels,
              style: {
                fontWeight: "400",
                fontSize: compact ? "10px" : "12px",
                color: colors.axisText,
              },
              ...(props.dataAsPercentage
                ? {
                    formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                      return (round(Number(this.value) * 100, 3) + "%").toString()
                    },
                  }
                : {}),
            },
            plotLines: [...plotLinesY, ...plotBandLinesY],
            plotBands: plotBandsY,
            opposite: index > 0,
            // Stack upwards in series order, so the first series of a stack sits at its base.
            // Highcharts' default puts the first series on top instead.
            reversedStacks: false,
          })),
      colorAxis: props.colorAxis
        ? props.colorAxis // Use custom color axis if provided
        : useColorAxis
          ? {
              min: 0,
              stops: [
                [0.0, getTailwindValue("--color-violet-100")],
                [0.15, getTailwindValue("--color-violet-300")],
                [0.25, getTailwindValue("--color-violet-400")],
                [0.5, getTailwindValue("--color-violet-500")],
                [1.0, getTailwindValue("--color-violet-600")],
              ],
            }
          : undefined,
      tooltip: {
        useHTML: true,
        shared: true,
        style: {
          color: "var(--color-fg-subtle)",
          fontSize: "13px",
          zIndex: 2147483647,
        },
        enabled: showTooltip,
        padding: 0,
        shadow: false,
        borderWidth: 0,
        outside: true,
        borderRadius: 12,
        // Surface (bg/border) is rendered in the formatter HTML via CSS `var()` tokens so it
        // updates live on light/dark toggle. The Highcharts box itself stays transparent.
        backgroundColor: "transparent",
        formatter:
          props.customTooltipFormatter ??
          function () {
            switch (type) {
              case ChartType.TimeSeries:
              case ChartType.Mixed:
              case ChartType.Waterfall:
              case ChartType.CategorySeries:
              case ChartType.StackedColumn:
                return dateGraphTooltipFormatter.call(this, dateType, {
                  dataAsPercentage: props.dataAsPercentage,
                  showCurrency: props.showCurrency,
                  currency: company?.currency ?? "DKK",
                  allDigits: props.showAllDigits,
                  stackSectionLabels: props.stackSectionLabels,
                  unsectionedLabel: props.unsectionedLabel,
                  plainCategories: props.plainTooltipCategories,
                })
              case ChartType.Pie:
                return doughnutGraphTooltipFormatter.call(
                  this,
                  props.dataAsPercentage,
                  subtitleLabel,
                )
              case ChartType.Scatter:
                return scatterGraphTooltipFormatter.call(this)
            }
          },
      },
      legend: {
        enabled: useDefaultLegend && !noLegend,
        floating: false,
        verticalAlign: legendAlignment === LegendAlignment.Top ? "top" : "middle",
        align: legendAlignment === LegendAlignment.Left ? "left" : "right",
        layout: legendAlignment === LegendAlignment.Top ? "horizontal" : "vertical",
        itemDistance: 12,
        // Matches the 12px / 4px-radius rounded square the custom legends draw via legendDot.
        symbolHeight: 12,
        symbolWidth: 12,
        symbolRadius: 4,
        margin: 6,
        itemStyle: {
          color: colors.legendText,
          fontWeight: "500",
          fontSize: compact ? "10px" : "12px",
        },
        itemHiddenStyle: {
          color: getTailwindValue("--color-fg-disabled"),
          fontWeight: "500",
          fontSize: compact ? "10px" : "12px",
        },
      },
      credits: {
        enabled: false,
      },
      accessibility: {
        enabled: false,
      },
    }
  }, [chartKey, resolvedTheme, disabledIds, company])

  if (props.colors) {
    // For some reason setting it crashes if the colors are undefined, so we can't set it conditionally above.
    options.colors = props.colors
  }

  const { onPointHover } = props
  const containerProps = useMemo(() => hoverContainerProps(onPointHover), [onPointHover])

  function handleLegendClick(
    entry: PieChartData | DateChartSeries | CategoryChartSeries | MixedChartData,
  ) {
    const index = disabledIds.indexOf(entry.name)

    if (index === -1) {
      setDisabledIds([...disabledIds, entry.name])
    } else {
      setDisabledIds(disabledIds.filter((_, i) => i !== index))
    }
  }

  const plot = (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartRef}
      containerProps={containerProps}
    />
  )

  if ((noLegend || useDefaultLegend) && !hasFrame) return plot

  function renderLegend(): ReactNode {
    if (noLegend || useDefaultLegend) return null
    switch (type) {
      case ChartType.Pie:
        return generateDoughnutChartLegend(
          finalData as PieChartData[],
          props.dataAsPercentage,
          compact,
          !!props.title &&
            legendAlignment !== LegendAlignment.Top &&
            legendAlignment !== LegendAlignment.Bottom,
          handleLegendClick,
        )
      case ChartType.TimeSeries:
      case ChartType.CategorySeries:
        return generateDateCategoryChartLegend(
          finalData as DateChartSeries[],
          compact,
          handleLegendClick,
          hasFrame,
        )
      case ChartType.Mixed:
        return generateMixedChartLegend(finalData as MixedChartData[], compact, handleLegendClick)
      default:
        return null
    }
  }

  const legend = renderLegend()

  if (legendAlignment === LegendAlignment.Top) {
    return (
      <div
        className={cn("flex w-full flex-col", hasFrame ? "gap-3" : "gap-2")}
        // A toolbar row or readout adds its own height, so the column grows with them.
        style={{ height: hasFrame ? undefined : height }}
      >
        {(props.toolbar != null || legend) && (
          <div className="flex w-full items-center gap-4">
            {props.toolbar != null && (
              <div className="flex shrink-0 items-center gap-2">{props.toolbar}</div>
            )}
            {/* Plain, the legend keeps its top-left place; with a toolbar or readout it goes right. */}
            <div className={cn("flex min-w-0 flex-1", hasFrame && "justify-end")}>{legend}</div>
          </div>
        )}
        {props.readout}
        <div className="w-full" {...props.plotProps}>
          {plot}
        </div>
      </div>
    )
  }

  const flexDirection = (legendAlignment && SIDE_LEGEND_DIRECTION[legendAlignment]) ?? "flex-row"

  return (
    <div
      className={`flex ${flexDirection} gap-2 justify-between w-full h-full`}
      style={{ height: height, alignItems: centerLegend ? "center" : "start" }}
    >
      {legend}
      <div style={{ width: legendAlignment === LegendAlignment.Bottom ? "100%" : height }}>
        {plot}
      </div>
    </div>
  )
}

export default SuperChart
